<script setup lang="ts">
import { useOnTimezoneChange } from '@entities/viewer'

import { LessonsActionModal } from '../model/lessons/enums'
import { useLessonsStore } from '../model/lessons/store'
import { abortLessonsApi, useLessonsApi } from '../api/lessons/index.hook'
import { useLessonsRecurringEvents } from '../model/lessons/lessons-recurring-events.hook'
import DisciplinePageLoader from './DisciplinePageLoader.vue'
import TableLessons from './lessons/TableLessons.vue'
import ModalLessonsDelete from './lessons/modals/ModalLessonsDelete.vue'
import ModalLessonsBulkEdit from './lessons/modals/ModalLessonsBulkEdit.vue'

defineOptions({
  name: 'LessonsListPage',
})

const route = useRoute()
const store = useLessonsStore()

/** The first list read shares the page loader with the discipline, so the page appears with its rows in place; a
 *  Search or a page change keeps the rows and only locks the controls. The list is dropped on leaving, so every
 *  arrival is a first read. */
const refIsFirstLoad = computed<boolean>(() => store.isLoading && store.getMeta === null)

const fetchLessons = (): void => {
  void useLessonsApi({ disciplineId: route.params.id as string })
}

onMounted(fetchLessons)

// Filters and pagination navigate by query; the middleware has already written them to the store.
watch(() => route.query, () => {
  if (route.name === 'disciplines-id-lessons') fetchLessons()
})

// Lesson times arrive pre-shifted into the admin's view timezone, so a timezone change needs a refetch.
useOnTimezoneChange(fetchLessons)

useLessonsRecurringEvents()

onBeforeUnmount(() => {
  abortLessonsApi()
  store.closeDeleteModal()
  store.closeActionModal()
  store.resetList()
})
</script>

<template>
  <DisciplinePageLoader :loading="refIsFirstLoad" />
  <TableLessons />
  <ModalLessonsDelete />
  <ModalLessonsBulkEdit v-if="store.getActionModal === LessonsActionModal.BulkEdit" />
</template>
